'use client';

import { RefreshCw } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { cn } from '@/lib/ui-kit/utils';
import { Tabs } from '@/lib/ui-kit/vercel-tabs';

export type OrderBookMobileTab = 'yes' | 'no';

export type OrderBookMobileTabsProps = {
  activeTab: OrderBookMobileTab;
  onTabChange: (tab: OrderBookMobileTab) => void;
  roundToCents: boolean;
  onRoundToCentsChange: (value: boolean) => void;
  onRefresh?: () => void;
  className?: string;
};

export const OrderBookMobileTabs = ({
  activeTab,
  onTabChange,
  roundToCents,
  onRoundToCentsChange,
  onRefresh,
  className,
}: OrderBookMobileTabsProps) => {
  const t = useTranslations('orderBook');

  const tabs = [
    { id: 'yes', label: t('tradeYes') },
    { id: 'no', label: t('tradeNo') },
  ];

  return (
    <div className={cn('flex items-center justify-between', className)}>
      <Tabs
        tabClassName="!body-small"
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={(tabId) => onTabChange(tabId as OrderBookMobileTab)}
        hideBottomBorder={false}
        activeIndicatorClassName="bg-white"
      />
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={onRefresh}
          className="bg-v2-btn-secondary text-v2-icon-primary hover:bg-v2-btn-secondary-hover hover:text-v2-icon-primary-hover flex h-8 w-8 items-center justify-center rounded-[8px] transition-colors"
          aria-label="Refresh order book"
        >
          <RefreshCw className="h-4 w-4" />
        </button>
        <button
          type="button"
          onClick={() => onRoundToCentsChange(!roundToCents)}
          className={cn(
            'flex h-8 w-8 items-center justify-center rounded-[8px] transition-colors',
            roundToCents
              ? 'bg-v2-btn-primary text-v2-btn-primary-text hover:bg-v2-btn-primary-hover'
              : 'bg-v2-btn-secondary text-v2-icon-primary hover:bg-v2-btn-secondary-hover hover:text-v2-icon-primary-hover',
          )}
          aria-label="Round to cents"
        >
          <span className="text-sm font-semibold">1¢</span>
        </button>
      </div>
    </div>
  );
};

/** Standalone action buttons for mobile order book */
export const OrderBookMobileActions = ({
  roundToCents,
  onRoundToCentsChange,
  onRefresh,
  className,
}: {
  roundToCents: boolean;
  onRoundToCentsChange: (value: boolean) => void;
  onRefresh?: () => void;
  className?: string;
}) => {
  return (
    <div className={cn('flex items-center gap-2', className)}>
      <button
        type="button"
        onClick={onRefresh}
        className="bg-v2-btn-secondary text-v2-icon-primary hover:bg-v2-btn-secondary-hover hover:text-v2-icon-primary-hover flex h-8 w-8 items-center justify-center rounded-[8px] transition-colors"
        aria-label="Refresh order book"
      >
        <RefreshCw className="h-4 w-4" />
      </button>
      <button
        type="button"
        onClick={() => onRoundToCentsChange(!roundToCents)}
        className={cn(
          'flex h-8 w-8 items-center justify-center rounded-[8px] transition-colors',
          roundToCents
            ? 'bg-v2-btn-primary text-v2-btn-primary-text hover:bg-v2-btn-primary-hover'
            : 'bg-v2-btn-secondary text-v2-icon-primary hover:bg-v2-btn-secondary-hover hover:text-v2-icon-primary-hover',
        )}
        aria-label="Round to cents"
      >
        <span className="text-base font-semibold leading-none">1¢</span>
      </button>
    </div>
  );
};
